<template>
  <div class="home-page">
    <NavBar />
    
    <div class="carousel-container">
      <a-skeleton v-if="banners.length === 0" :animation="true" :style="{ height: '776px' }">
        <a-skeleton-shape :style="{ width: '100%', height: '776px' }" shape="square" />
      </a-skeleton>
      <a-carousel
        v-else
        :auto-play="true"
        animation-name="fade"
        show-arrow="hover"
        indicator-type="slider"
        class="carousel-wrapper"
        :style="{
          height: '776px'
        }"
      >
        <a-carousel-item v-for="banner in banners" :key="banner.id">
          <a :href="banner.url" :style="{ display: 'block', width: '100%', height: '100%' }">
            <img
              :src="banner.image_url"
              :style="{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }"
            />
          </a>
        </a-carousel-item>
      </a-carousel>
    </div>
    
    <!-- 按分类显示文章 -->
    <div class="article-section" v-for="category in categories" :key="category.id">
      <div class="section-header">
        <h2 class="section-title">{{ category.name }}</h2>
      </div>
      
      <div class="article-list">
        <!-- 骨架屏 -->
        <template v-if="!categoryArticles[category.id]">
          <div class="article-item" v-for="i in 4" :key="'skeleton-' + i">
            <a-skeleton :animation="true">
              <div style="display: flex; gap: 24px;">
                <a-skeleton-shape :style="{ width: '160px', height: '100px', flexShrink: 0 }" shape="square" />
                <div style="flex: 1;">
                  <a-skeleton-line :rows="2" :widths="['80%', '40%']" />
                </div>
              </div>
            </a-skeleton>
          </div>
        </template>
        <!-- 文章列表 -->
        <template v-else>
          <div 
            class="article-item" 
            v-for="article in categoryArticles[category.id]" 
            :key="article.id"
            @click="$router.push(`/article/${article.id}`)"
          >
            <div class="item-thumb">
              <img :src="article.cover_url" :alt="article.title">
            </div>
            <div class="item-info">
              <h3 class="item-title">{{ article.title }}</h3>
              <div class="item-meta">
                <span>{{ article.contact_name || '匿名' }}</span>
                <span class="dot"></span>
                <span>{{ formatTime(article.createtime) }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
      
      <div class="more-section">
        <span class="load-more" @click="$router.push(`/articles/${category.id}`)">查看全部 →</span>
      </div>
    </div>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { getBannerList, getArticleList, getArticleCategories } from '@/api/index.js'

export default {
  name: 'HomePage',
  components: {
    NavBar,
    Footer
  },
  data() {
    return {
      banners: [],
      categories: [],
      categoryArticles: {}
    }
  },
  mounted() {
    this.loadBanners()
    this.loadCategories()
  },
  methods: {
    async loadBanners() {
      try {
        const result = await getBannerList({ limit: 10 })
        if (result.code === 1 && result.data && result.data.list) {
          this.banners = result.data.list
        }
      } catch (error) {
        console.error('加载轮播图失败:', error)
      }
    },
    async loadCategories() {
      try {
        const result = await getArticleCategories()
        if (result.code === 1 && result.data) {
          this.categories = result.data
          // 加载每个分类的文章
          this.categories.forEach(cat => {
            this.loadArticlesByCategory(cat.id)
          })
        }
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    },
    async loadArticlesByCategory(categoryId) {
      try {
        const result = await getArticleList({ limit: 6, category_id: categoryId })
        if (result.code === 1 && result.data && result.data.list) {
          this.categoryArticles[categoryId] = result.data.list
          // 触发响应式更新
          this.categoryArticles = { ...this.categoryArticles }
        }
      } catch (error) {
        console.error('加载文章失败:', error)
      }
    },
    formatTime(timestamp) {
      const date = new Date(timestamp * 1000)
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${month}月${day}日`
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  background: #f7f8fa;
}

.carousel-container {
  margin-top: -76px;
  position: relative;
  z-index: 1;
}

.carousel-wrapper {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.carousel-wrapper a {
  cursor: pointer;
}

/* 文章区域 */
.article-section {
  padding: 60px 40px;
  max-width: 900px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 2px solid #111;
}

.section-title {
  font-size: 28px;
  font-weight: 800;
  color: #111;
  margin: 0;
}

.article-list {
  display: flex;
  flex-direction: column;
}

.article-item {
  display: flex;
  gap: 24px;
  padding: 24px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s ease;
}

.article-item:hover {
  background: #fafafa;
  margin: 0 -20px;
  padding: 24px 20px;
}

.item-thumb {
  width: 160px;
  height: 100px;
  flex-shrink: 0;
  overflow: hidden;
}

.item-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.item-title {
  font-size: 18px;
  font-weight: 600;
  color: #111;
  line-height: 1.5;
  margin: 0 0 12px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.article-item:hover .item-title {
  text-decoration: underline;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #999;
}

.item-meta .dot {
  width: 3px;
  height: 3px;
  background: #ccc;
  border-radius: 50%;
}

.more-section {
  margin-top: 40px;
  padding-top: 20px;
  text-align: right;
}

.load-more {
  font-size: 14px;
  font-weight: 600;
  color: #111;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.load-more:hover {
  opacity: 0.6;
}

@media (max-width: 768px) {
  .article-section {
    padding: 40px 20px;
  }
  
  .section-title {
    font-size: 22px;
  }
  
  .article-item {
    flex-direction: column;
    gap: 16px;
  }
  
  .item-thumb {
    width: 100%;
    height: 180px;
  }
  
  .item-title {
    font-size: 16px;
  }
}
</style>
